import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { shortDate, units } from "@/lib/format"
import { type NumberedAction, stockMarks } from "../lib/actions"
import { STOCK_SOURCE_LABEL, stockSource } from "../lib/cardText"
import { addDays, daysBetween } from "../lib/dates"
import { type StockLine, stockLines } from "../lib/stockLines"
import type { QuickViewData } from "../types"
import { ActionKey, MARKED_TOP, actionLines } from "./ActionMarks"

const AXIS_TICK = { fontSize: 10, fill: "var(--chart-axis)" }

function LegendItem({ line }: { line: StockLine }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-0.5 w-3 rounded-sm" style={{ background: line.color }} />
      {line.label}
    </span>
  )
}

// Projected stock over the buying window (two lead times). With a plan, two
// lines to compare: the plan's (cut where the plan ends) and the stock run
// down at the average daily sales of the last 90 days (to the window end).
// Without a plan, the average's line alone, and a note under the title says
// so. Point N is the stock at the start of day today + N, so an arrival shows
// as a step up. Dashed line: the day an order placed today would land
// (labelled "if ordered today" so it is not mistaken for a shipment already
// on the way; hidden when the Purchase Plan has no order to place). Red dots: the day each line runs out (stock is zero by
// the end of it), so a dot sits one point before its line first touches
// zero; hovering one says which line. Numbered dashed lines: Push review
// actions dated inside the window. Shown as a tab of GraphsSection.
export default function StockChart({ data, actions }: { data: QuickViewData; actions: NumberedAction[] }) {
  const { buying, purchase, average_sales, today } = data
  const source = stockSource(buying, average_sales)
  const chart = stockLines(buying, average_sales, today)
  if (source === null || chart === null) {
    return (
      <p className="text-sm text-foreground">
        No plan for this market and nothing sold or gifted in the last 90 days, so there is no stock line.
      </p>
    )
  }

  const { points, lines, last } = chart
  const marks = stockMarks(actions, today, last)
  const compare = lines.length > 1
  const landsDay = daysBetween(today, buying.order_lands_on)
  const dayLabel = (day: number) => (day === 0 ? "Today" : shortDate(addDays(today, day), today))

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3 text-xs text-foreground">
        {compare ? (
          <span className="flex items-center gap-4">
            {lines.map((line) => <LegendItem key={line.key} line={line} />)}
          </span>
        ) : (
          <span>{STOCK_SOURCE_LABEL[source]}</span>
        )}
        <span>Until {shortDate(addDays(today, last), today)}</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={points} margin={{ top: marks.length > 0 ? MARKED_TOP : 16, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
          <XAxis dataKey="day" type="number" domain={[0, last]} allowDecimals={false} tick={AXIS_TICK} tickFormatter={dayLabel} />
          <YAxis width={48} tick={AXIS_TICK} tickFormatter={(value: number) => units(value)} />
          <Tooltip
            labelFormatter={(day) => dayLabel(Number(day))}
            formatter={(value, name) => [units(Number(value)), compare ? name : "Stock"]}
            itemSorter={(item) => lines.findIndex((line) => line.key === item.dataKey)}
          />
          {(purchase?.quantity ?? 0) > 0 && landsDay <= last && (
            <ReferenceLine
              x={landsDay}
              stroke="var(--chart-axis)"
              strokeDasharray="4 3"
              label={({ viewBox }: { viewBox?: { x?: number; y?: number } }) => (
                // On the chart's last day the text ends at the line so it stays inside the chart.
                <text x={viewBox?.x} y={(viewBox?.y ?? 0) - 5} textAnchor={landsDay >= last ? "end" : "middle"} fontSize={10} fill="var(--chart-axis)">
                  {`Order lands ${shortDate(buying.order_lands_on, today)} if ordered today`}
                </text>
              )}
            />
          )}
          {actionLines(marks)}
          {lines.map((line) => (
            <Line key={line.key} dataKey={line.key} name={line.label} type="linear" stroke={line.color} strokeWidth={2} dot={false} isAnimationActive={false} />
          ))}
          {lines.map((line) => line.stockoutDay !== null && line.stockoutOn && (
            <ReferenceDot
              key={`out-${line.key}`}
              x={line.stockoutDay}
              y={0}
              shape={({ cx, cy }: { cx?: number; cy?: number }) => (
                <circle cx={cx} cy={cy} r={5} fill="var(--destructive)" stroke={compare ? line.color : "var(--card)"} strokeWidth={2}>
                  <title>{`${line.label}: out of stock ${shortDate(line.stockoutOn ?? "", today)}`}</title>
                </circle>
              )}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <ActionKey marks={marks} today={today} />
    </div>
  )
}
